"use client";

import {
  type ColumnDef,
  type ColumnFiltersState,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  type TableOptions,
  type TableState,
  type Updater,
  type VisibilityState,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  type Parser,
  type UseQueryStateOptions,
  createParser,
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState,
  useQueryStates,
} from "nuqs";
import * as React from "react";

import { useDebouncedCallback } from "@/hooks/use-debounced-callback";
import { getSortingStateParser } from "@/lib/parsers";
import type { ExtendedColumnSort, TableConfig } from "@/types/data-table";
import { useTableStateSync } from "@/hooks/use-table-state-sync";
import { MainTableState } from "@/lib/utils/table-state-utils";

const PAGE_KEY = "page";
const PER_PAGE_KEY = "perPage";
const SORT_KEY = "sort";
const ARRAY_SEPARATOR = ",";
const DEBOUNCE_MS = 300;
const THROTTLE_MS = 50;
const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_SORTING = [{ id: 'createdAt', desc: true }];

interface UseDataTableProps<TData>
  extends Omit<
    TableOptions<TData>,
    | "state"
    | "pageCount"
    | "getCoreRowModel"
    | "manualFiltering"
    | "manualPagination"
    | "manualSorting"
  > {
  data: TData[];
  columns: ColumnDef<TData>[];
  total: number;
  config?: TableConfig;
  onTableStateChange: (state: MainTableState) => void;
  getRowId: (row: TData) => string;
  history?: "push" | "replace";
  throttleMs?: number;
  clearOnDefault?: boolean;
  enableAdvancedFilter?: boolean;
  scroll?: boolean;
  shallow?: boolean;
  startTransition?: any  //React.TransitionStartFunction;
}

export function useDataTable<TData>(props: UseDataTableProps<TData>) {
  const {
    columns,
    total,
    config = {},
    onTableStateChange,
    getRowId,
    history = "replace",
    throttleMs = THROTTLE_MS,
    clearOnDefault = false,
    enableAdvancedFilter = false,
    scroll = false,
    shallow = true,
    startTransition,
    ...tableProps
  } = props;

  const {
    pageSize = DEFAULT_PAGE_SIZE,
    debounceMs = DEBOUNCE_MS,
    defaultSorting = DEFAULT_SORTING,
    clientMode = false,
  } = config;

  const queryStateOptions = React.useMemo<
    Omit<UseQueryStateOptions<string>, "parse">
  >(
    () => ({
      history,
      scroll,
      shallow,
      throttleMs,
      debounceMs,
      clearOnDefault,
      startTransition,
    }),
    [
      history,
      scroll,
      shallow,
      throttleMs,
      debounceMs,
      clearOnDefault,
      startTransition,
    ],
  );

  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});

  const [page, setPage] = useQueryState(
    PAGE_KEY,
    parseAsInteger.withOptions(queryStateOptions).withDefault(1),
  );
  const [perPage, setPerPage] = useQueryState(
    PER_PAGE_KEY,
    parseAsInteger
      .withOptions(queryStateOptions)
      .withDefault(pageSize),
  );

  // Calculate pageCount using the actual perPage from URL, not the default pageSize
  const pageCount = React.useMemo(() => Math.ceil(total / perPage), [total, perPage]);

  // Reset to page 1 when page size changes
  React.useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
  }, [perPage]); // Only depend on perPage, not page to avoid infinite loop

  const pagination: PaginationState = React.useMemo(() => {
    return {
      pageIndex: page - 1, // zero-based index -> one-based index
      pageSize: perPage,
    };
  }, [page, perPage]);

  const onPaginationChange = React.useCallback(
    (updaterOrValue: Updater<PaginationState>) => {
      if (typeof updaterOrValue === "function") {
        const newPagination = updaterOrValue(pagination);
        void setPage(newPagination.pageIndex + 1);
        void setPerPage(newPagination.pageSize);
      } else {
        void setPage(updaterOrValue.pageIndex + 1);
        void setPerPage(updaterOrValue.pageSize);
      }
    },
    [pagination, setPage, setPerPage],
  );

  const columnIds = React.useMemo(() => {
    return new Set(
      columns.map((column) => column.id).filter(Boolean) as string[],
    );
  }, [columns]);

  const [sorting, setSorting] = useQueryState(
    SORT_KEY,
    getSortingStateParser<TData>(columnIds)
      .withOptions(queryStateOptions)
      .withDefault(defaultSorting as ExtendedColumnSort<TData>[]),
  );

  const onSortingChange = React.useCallback(
    (updaterOrValue: Updater<SortingState>) => {
      if (typeof updaterOrValue === "function") {
        const newSorting = updaterOrValue(sorting);
        setSorting(newSorting as ExtendedColumnSort<TData>[]);
      } else {
        setSorting(updaterOrValue as ExtendedColumnSort<TData>[]);
      }
    },
    [sorting, setSorting],
  );

  const filterableColumns = React.useMemo(() => {
    if (enableAdvancedFilter) return [];

    return columns.filter((column) => column.enableColumnFilter);
  }, [columns, enableAdvancedFilter]);

  const filterParsers = React.useMemo(() => {
    if (enableAdvancedFilter) return {};

    return filterableColumns.reduce<
      Record<string, Parser<string> | Parser<string[]> | Parser<any>>
    >((acc, column) => {
      // Date, number, and money filters need special handling for complex objects with conditions
      if (column.meta?.variant === 'date' || column.meta?.variant === 'dateRange' || column.meta?.variant === 'number' || column.meta?.variant === 'money') {
        acc[column.id ?? ""] = createParser({
          parse: (value: string) => {
            try {
              return JSON.parse(value);
            } catch {
              return null;
            }
          },
          serialize: (value: any) => {
            if (!value) return '';
            return JSON.stringify(value);
          },
        }).withOptions(queryStateOptions);
      } else if (column.meta?.options || column.meta?.variant === 'multiSelect' || column.meta?.variant === 'multiAsyncSelect') {
        // Array-based filters (multi-select, options-based filters, async filters)
        acc[column.id ?? ""] = parseAsArrayOf(
          parseAsString,
          ARRAY_SEPARATOR,
        ).withOptions(queryStateOptions);
      } else {
        acc[column.id ?? ""] = parseAsString.withOptions(queryStateOptions);
      }
      return acc;
    }, {});
  }, [filterableColumns, queryStateOptions, enableAdvancedFilter]);

  const [filterValues, setFilterValues] = useQueryStates(filterParsers);

  const debouncedSetFilterValues = useDebouncedCallback(
    (values: typeof filterValues) => {
      void setPage(1);
      void setFilterValues(values);
    },
    debounceMs,
  );

  const initialColumnFilters: ColumnFiltersState = React.useMemo(() => {
    if (enableAdvancedFilter) return [];

    return Object.entries(filterValues).reduce<ColumnFiltersState>(
      (filters, [key, value]) => {
        if (value !== null) {
          // Check column variant to determine processing
          const column = filterableColumns.find(col => col.id === key);
          const isDateFilter = column?.meta?.variant === 'date' || column?.meta?.variant === 'dateRange';
          const isNumberFilter = column?.meta?.variant === 'number';
          const isMoneyFilter = column?.meta?.variant === 'money';

          // For date, number, and money filters, pass the value through as-is (already parsed correctly)
          const processedValue = (isDateFilter || isNumberFilter || isMoneyFilter)
            ? value
            : Array.isArray(value)
              ? value
              : typeof value === "string" && /[^a-zA-Z0-9]/.test(value)
                ? value.split(/[^a-zA-Z0-9]+/).filter(Boolean)
                : [value];

          filters.push({
            id: key,
            value: processedValue,
          });
        }
        return filters;
      },
      [],
    );
  }, [filterValues, enableAdvancedFilter, filterableColumns]);

  const [columnFilters, setColumnFilters] =
    React.useState<ColumnFiltersState>(initialColumnFilters);

  const onColumnFiltersChange = React.useCallback(
    (updaterOrValue: Updater<ColumnFiltersState>) => {
      if (enableAdvancedFilter) return;

      setColumnFilters((prev) => {
        const next =
          typeof updaterOrValue === "function"
            ? updaterOrValue(prev)
            : updaterOrValue;

        const filterUpdates = next.reduce<
          Record<string, string | string[] | any | null>
        >((acc, filter) => {
          const column = filterableColumns.find((col) => col.id === filter.id);

          if (column) {
            // For date, number, and money filters, pass the value through as-is
            const isDateFilter = column.meta?.variant === 'date' || column.meta?.variant === 'dateRange';
            const isNumberFilter = column.meta?.variant === 'number';
            const isMoneyFilter = column.meta?.variant === 'money';
            const processedValue = (isDateFilter || isNumberFilter || isMoneyFilter) ? filter.value : (filter.value as string | string[]);

            acc[filter.id] = processedValue;
          } else {
            // If column not found in filterableColumns, still try to set it
            // This handles edge cases where the column might be dynamically added
            acc[filter.id] = filter.value;
          }
          return acc;
        }, {});

        for (const prevFilter of prev) {
          if (!next.some((filter) => filter.id === prevFilter.id)) {
            filterUpdates[prevFilter.id] = null;
          }
        }

        debouncedSetFilterValues(filterUpdates);
        return next;
      });
    },
    [debouncedSetFilterValues, filterableColumns, enableAdvancedFilter],
  );

  const table = useReactTable({
    ...tableProps,
    columns,
    initialState: {
      sorting: defaultSorting,
    },
    pageCount,
    state: {
      pagination,
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
    },
    defaultColumn: {
      ...tableProps.defaultColumn,
      enableColumnFilter: false,
    },
    enableRowSelection: true,
    getRowId,
    onRowSelectionChange: setRowSelection,
    onPaginationChange,
    onSortingChange,
    onColumnFiltersChange,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    manualPagination: !clientMode,
    manualSorting: !clientMode,
    manualFiltering: !clientMode,
    rowCount: total,
  });

  // Use the state sync hook
  useTableStateSync(table, onTableStateChange, debounceMs);

  return {
    table,
    shallow,
    throttleMs
  };
}
